import { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import claudiaImage from "../../assets/images/claudia.jpg";
import { supabase } from "../../lib/supabaseClient";

const colors = {
    cream: "#fffaf2",
    sand: "#f7f1e8",
    paper: "rgba(255, 250, 242, 0.92)",
    white: "rgba(255,255,255,0.72)",
    ink: "#2a2621",
    muted: "rgba(42, 38, 33, 0.66)",
    softMuted: "rgba(42, 38, 33, 0.48)",
    brown: "#9b7653",
    brownDark: "#6f5136",
    brownDarker: "#543b26",
    blue: "#2f91b8",
    border: "rgba(155,118,83,0.15)",
};

const fallbackSettings = {
    eyebrow: "Kontakt",
    headline: "Wir freuen uns auf Sie.",
    intro:
        "Besuchen Sie uns direkt im Herzen von Hooksiel oder melden Sie sich telefonisch oder per E-Mail.",
    business_name: "Hooksieler Lädchen",
    contact_person: "Claudia Don",
    street: "Lange Straße 37",
    postal_city: "26434 Hooksiel",
    phone_label: "04425 – 353",
    phone_href: "+494425353",
    fax: "04425 – 6759821",
    email: "info@hooksieler-laedchen.de",
    image_url: "",
    image_alt: "Claudia Don im Hooksieler Lädchen",
    image_badge_label: "Persönlich vor Ort",
    image_badge_text: "Claudia Don freut sich auf Ihren Besuch.",
    opening_hours: [
        { day: "Montag", time: "Ruhetag" },
        { day: "Dienstag", time: "11:00 – 17:00 Uhr" },
        { day: "Mittwoch", time: "11:00 – 17:00 Uhr" },
        { day: "Donnerstag", time: "11:00 – 17:00 Uhr" },
        { day: "Freitag", time: "11:00 – 17:00 Uhr" },
        { day: "Samstag", time: "11:00 – 17:00 Uhr" },
        { day: "Sonntag", time: "11:00 – 17:00 Uhr" },
    ],
};

async function getContactSettings() {
    const { data, error } = await supabase
        .from("contact_settings")
        .select("*")
        .eq("id", "main")
        .single();

    if (error) {
        throw new Error(error.message);
    }

    return data;
}

function ContactSection() {
    const [settings, setSettings] = useState(fallbackSettings);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        async function loadSettings() {
            try {
                setIsLoading(true);
                setErrorMessage("");

                const data = await getContactSettings();

                setSettings({
                    ...fallbackSettings,
                    ...data,
                    opening_hours:
                        Array.isArray(data.opening_hours) &&
                        data.opening_hours.length > 0
                            ? data.opening_hours
                            : fallbackSettings.opening_hours,
                });
            } catch (error) {
                console.error(error);
                setErrorMessage(
                    "Kontaktinformationen konnten gerade nicht geladen werden."
                );
            } finally {
                setIsLoading(false);
            }
        }

        loadSettings();
    }, []);

    const imageSource = settings.image_url || claudiaImage;

    return (
        <Box
            component="section"
            id="kontakt"
            sx={{
                position: "relative",
                overflow: "hidden",
                py: {
                    xs: 8,
                    md: 13,
                },
                background:
                    "linear-gradient(180deg, #eef7fa 0%, #fffaf2 45%, #f7f1e8 100%)",
            }}
        >
            <Box
                aria-hidden="true"
                sx={{
                    position: "absolute",
                    top: {
                        xs: 80,
                        md: 120,
                    },
                    right: {
                        xs: -150,
                        md: -120,
                    },
                    width: {
                        xs: 300,
                        md: 460,
                    },
                    height: {
                        xs: 300,
                        md: 460,
                    },
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(47,145,184,0.13) 0%, rgba(47,145,184,0) 68%)",
                    pointerEvents: "none",
                }}
            />

            <Container
                maxWidth={false}
                sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 1180,
                    mx: "auto",
                    px: {
                        xs: 2,
                        sm: 3,
                        md: 4,
                    },
                }}
            >
                {isLoading ? (
                    <Paper
                        elevation={0}
                        sx={{
                            py: 8,
                            px: 3,
                            borderRadius: 5,
                            backgroundColor: colors.paper,
                            border: `1px solid ${colors.border}`,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                        }}
                    >
                        <CircularProgress sx={{ color: colors.brownDark }} />

                        <Typography
                            sx={{
                                fontFamily: '"Nunito Sans", sans-serif',
                                fontSize: 15,
                                fontWeight: 800,
                                color: colors.muted,
                            }}
                        >
                            Kontaktinformationen werden geladen...
                        </Typography>
                    </Paper>
                ) : (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "1.02fr 0.98fr",
                            },
                            gap: {
                                xs: 4,
                                md: 7,
                            },
                            alignItems: "center",
                        }}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                overflow: "hidden",
                                borderRadius: {
                                    xs: 5,
                                    md: 7,
                                },
                                boxShadow: "0 28px 90px rgba(78,54,30,0.18)",
                                backgroundColor: colors.sand,
                            }}
                        >
                            <Box
                                component="img"
                                src={imageSource}
                                alt={settings.image_alt}
                                sx={{
                                    width: "100%",
                                    height: {
                                        xs: "auto",
                                        md: 720,
                                    },
                                    display: "block",
                                    objectFit: {
                                        xs: "contain",
                                        md: "cover",
                                    },
                                    objectPosition: "center",
                                }}
                            />

                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    background:
                                        "linear-gradient(180deg, rgba(0,0,0,0.02) 40%, rgba(0,0,0,0.42) 100%)",
                                }}
                            />

                            <Box
                                sx={{
                                    position: "absolute",
                                    left: {
                                        xs: 18,
                                        md: 24,
                                    },
                                    right: {
                                        xs: 18,
                                        md: "auto",
                                    },
                                    bottom: {
                                        xs: 18,
                                        md: 24,
                                    },
                                    maxWidth: 380,
                                    p: {
                                        xs: 2,
                                        md: 2.4,
                                    },
                                    borderRadius: 4,
                                    backgroundColor: "rgba(255,250,242,0.9)",
                                    backdropFilter: "blur(8px)",
                                    boxShadow: "0 16px 45px rgba(0,0,0,0.18)",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily:
                                            '"Nunito Sans", sans-serif',
                                        fontSize: 12,
                                        fontWeight: 900,
                                        letterSpacing: "0.14em",
                                        textTransform: "uppercase",
                                        color: colors.brown,
                                    }}
                                >
                                    {settings.image_badge_label}
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.5,
                                        fontFamily: '"Quicksand", sans-serif',
                                        fontSize: {
                                            xs: "1.35rem",
                                            md: "1.6rem",
                                        },
                                        lineHeight: 1.2,
                                        fontWeight: 700,
                                        color: colors.ink,
                                    }}
                                >
                                    {settings.image_badge_text}
                                </Typography>
                            </Box>
                        </Box>

                        <Paper
                            elevation={0}
                            sx={{
                                p: {
                                    xs: 3,
                                    md: 4.5,
                                },
                                borderRadius: {
                                    xs: 5,
                                    md: 7,
                                },
                                backgroundColor: colors.paper,
                                border: `1px solid ${colors.border}`,
                                boxShadow: "0 22px 70px rgba(78,54,30,0.08)",
                            }}
                        >
                            {errorMessage && (
                                <Alert
                                    severity="warning"
                                    sx={{
                                        mb: 3,
                                        borderRadius: 3,
                                        fontFamily:
                                            '"Nunito Sans", sans-serif',
                                    }}
                                >
                                    {errorMessage}
                                </Alert>
                            )}

                            <Typography
                                component="p"
                                sx={{
                                    mb: 1.1,
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontSize: 13,
                                    fontWeight: 900,
                                    letterSpacing: "0.16em",
                                    textTransform: "uppercase",
                                    color: colors.brown,
                                }}
                            >
                                {settings.eyebrow}
                            </Typography>

                            <Typography
                                component="h2"
                                sx={{
                                    mb: 2.5,
                                    fontFamily: '"Quicksand", sans-serif',
                                    fontSize: {
                                        xs: "2.35rem",
                                        sm: "3rem",
                                        md: "3.8rem",
                                    },
                                    lineHeight: 1,
                                    fontWeight: 700,
                                    letterSpacing: "-0.06em",
                                    color: colors.ink,
                                }}
                            >
                                {settings.headline}
                            </Typography>

                            <Typography
                                sx={{
                                    mb: 4,
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontSize: {
                                        xs: 16.5,
                                        md: 18,
                                    },
                                    lineHeight: 1.75,
                                    color: colors.muted,
                                }}
                            >
                                {settings.intro}
                            </Typography>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "1fr 1fr",
                                    },
                                    gap: 2,
                                    mb: 4,
                                }}
                            >
                                <InfoBlock label="Adresse">
                                    {settings.business_name}
                                    <br />
                                    {settings.contact_person}
                                    <br />
                                    {settings.street}
                                    <br />
                                    {settings.postal_city}
                                </InfoBlock>

                                <InfoBlock label="Telefon">
                                    <Typography
                                        component="a"
                                        href={`tel:${settings.phone_href}`}
                                        sx={linkTextStyles}
                                    >
                                        {settings.phone_label}
                                    </Typography>
                                </InfoBlock>

                                {settings.fax && (
                                    <InfoBlock label="Fax">
                                        {settings.fax}
                                    </InfoBlock>
                                )}

                                <InfoBlock label="E-Mail">
                                    <Typography
                                        component="a"
                                        href={`mailto:${settings.email}`}
                                        sx={{
                                            ...linkTextStyles,
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {settings.email}
                                    </Typography>
                                </InfoBlock>
                            </Box>

                            <Divider
                                sx={{
                                    mb: 3,
                                    borderColor: "rgba(42,38,33,0.1)",
                                }}
                            />

                            <Box sx={{ mb: 4 }}>
                                <Typography
                                    sx={{
                                        mb: 1.6,
                                        fontFamily:
                                            '"Nunito Sans", sans-serif',
                                        fontSize: 13,
                                        fontWeight: 900,
                                        letterSpacing: "0.12em",
                                        textTransform: "uppercase",
                                        color: colors.brown,
                                    }}
                                >
                                    Öffnungszeiten
                                </Typography>

                                <Stack spacing={1}>
                                    {settings.opening_hours.map((item) => (
                                        <Box
                                            key={item.day}
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns:
                                                    "110px 1fr",
                                                gap: 2,
                                                py: 1,
                                                px: 1.4,
                                                borderRadius: 3,
                                                backgroundColor:
                                                    "rgba(255,255,255,0.46)",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontFamily:
                                                        '"Nunito Sans", sans-serif',
                                                    fontSize: 14,
                                                    fontWeight: 900,
                                                    color: colors.ink,
                                                }}
                                            >
                                                {item.day}
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontFamily:
                                                        '"Nunito Sans", sans-serif',
                                                    fontSize: 14,
                                                    fontWeight: 800,
                                                    color:
                                                        item.time
                                                            ?.toLowerCase()
                                                            .includes(
                                                                "geschlossen"
                                                            )
                                                            ? colors.softMuted
                                                            : colors.muted,
                                                }}
                                            >
                                                {item.time}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: {
                                        xs: "column",
                                        sm: "row",
                                    },
                                    gap: 1.5,
                                }}
                            >
                                <Button
                                    href={`tel:${settings.phone_href}`}
                                    variant="contained"
                                    sx={primaryButtonStyles}
                                >
                                    Anrufen
                                </Button>

                                <Button
                                    href={`mailto:${settings.email}`}
                                    variant="outlined"
                                    sx={secondaryButtonStyles}
                                >
                                    E-Mail schreiben
                                </Button>
                            </Box>
                        </Paper>
                    </Box>
                )}
            </Container>
        </Box>
    );
}

function InfoBlock({ label, children }) {
    return (
        <Box>
            <Typography
                sx={{
                    mb: 0.4,
                    fontFamily: '"Nunito Sans", sans-serif',
                    fontSize: 13,
                    fontWeight: 900,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: colors.brown,
                }}
            >
                {label}
            </Typography>

            <Typography
                component="div"
                sx={{
                    fontFamily: '"Nunito Sans", sans-serif',
                    fontSize: {
                        xs: 17,
                        md: 18.5,
                    },
                    lineHeight: 1.55,
                    fontWeight: 800,
                    color: colors.ink,
                }}
            >
                {children}
            </Typography>
        </Box>
    );
}

const linkTextStyles = {
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: {
        xs: 17,
        md: 18.5,
    },
    lineHeight: 1.55,
    fontWeight: 800,
    color: colors.ink,
    textDecoration: "none",

    "&:hover": {
        color: colors.brownDark,
    },
};

const primaryButtonStyles = {
    minHeight: 52,
    px: 3.5,
    borderRadius: 999,
    backgroundColor: colors.brownDark,
    color: "#fff",
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: 14,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    boxShadow: "0 14px 30px rgba(111,81,54,0.22)",

    "&:hover": {
        backgroundColor: colors.brownDarker,
        boxShadow: "0 18px 38px rgba(111,81,54,0.28)",
    },
};

const secondaryButtonStyles = {
    minHeight: 52,
    px: 3.5,
    borderRadius: 999,
    borderWidth: 2,
    borderColor: colors.brown,
    color: colors.brownDark,
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: 14,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.05em",

    "&:hover": {
        borderWidth: 2,
        borderColor: colors.brownDark,
        backgroundColor: "rgba(155,118,83,0.08)",
    },
};

export default ContactSection;